/**
 * Teacher Students Page
 *
 * Page for teachers to view all students with their personal info,
 * class assignments, level info, and payment status.
 */

import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Spinner } from "../../components/ui";
import { useSyncReceipts } from "../../hooks/usePayment";
import { useTeacherClasses, useTeacherStudents } from "../../hooks/useTeacher";
import type { StudentForTeacher } from "../../types/teacher";

const LEVELS = ["A1", "A2", "B1", "B2", "C1", "C2"];

// Format date for display
function formatDate(dateStr: string | null): string {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  return date.toLocaleDateString("pt-BR");
}

// Get payment status info - derived from payment counts
function getPaymentStatusInfo(
  paymentSummary: {
    totalPaid: number;
    totalPending: number;
    totalOverdue: number;
  } | null,
): {
  label: string;
  bgClass: string;
  textClass: string;
  status: string;
} {
  if (!paymentSummary) {
    return {
      label: "No module",
      bgClass: "bg-earth-100",
      textClass: "text-earth-700",
      status: "no_module",
    };
  }

  // Priority: Overdue > Paid (no overdue) > Pending
  if (paymentSummary.totalOverdue > 0) {
    return {
      label: "Overdue",
      bgClass: "bg-red-100",
      textClass: "text-red-700",
      status: "overdue",
    };
  }

  // If no overdue and has paid something, show as "Paid"
  if (paymentSummary.totalOverdue === 0 && paymentSummary.totalPaid > 0) {
    return {
      label: "Paid",
      bgClass: "bg-emerald-100",
      textClass: "text-emerald-700",
      status: "paid",
    };
  }

  // Only pending (no paid, no overdue)
  if (paymentSummary.totalPending > 0) {
    return {
      label: "Pending",
      bgClass: "bg-amber-100",
      textClass: "text-amber-700",
      status: "pending",
    };
  }

  return {
    label: "Not set",
    bgClass: "bg-earth-100",
    textClass: "text-earth-700",
    status: "unknown",
  };
}

// Student row component
function StudentRow({
  student,
  onClick,
}: {
  student: StudentForTeacher;
  onClick: () => void;
}) {
  const paymentInfo = getPaymentStatusInfo(student.paymentSummary);

  return (
    <tr
      onClick={onClick}
      className="border-b border-earth-100 hover:bg-olive-50 cursor-pointer transition-colors"
    >
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-earth-200 rounded-full flex items-center justify-center text-earth-600 font-semibold text-sm">
            {student.fullName.charAt(0)}
          </div>
          <div className="min-w-0">
            <p className="font-medium text-earth-800 truncate">
              {student.fullName}
            </p>
            <p className="text-xs text-earth-700 truncate">{student.email}</p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        <span className="inline-flex items-center px-2 py-1 bg-olive-100 text-olive-800 rounded text-sm font-medium">
          {student.levelCode || "-"}
        </span>
      </td>
      <td className="px-4 py-3 text-sm text-earth-700">
        {student.classGroupName || (
          <span className="text-earth-700 italic">No class</span>
        )}
      </td>
      <td className="px-4 py-3 text-sm text-earth-600">
        {student.levelName || "-"}
      </td>
      <td className="px-4 py-3">
        <span
          className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${paymentInfo.bgClass} ${paymentInfo.textClass}`}
        >
          {paymentInfo.label}
        </span>
      </td>
      <td className="px-4 py-3 text-right">
        <i className="ri-arrow-right-s-line text-earth-400"></i>
      </td>
    </tr>
  );
}

// Student detail modal/panel
function StudentDetailPanel({
  student,
  onClose,
}: {
  student: StudentForTeacher;
  onClose: () => void;
}) {
  const paymentInfo = getPaymentStatusInfo(student.paymentSummary);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/30" onClick={onClose}></div>

      {/* Panel */}
      <div className="relative w-full max-w-md h-full bg-white shadow-xl overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-earth-200 px-5 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-earth-800">
            Student Details
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-earth-100 rounded-lg transition-colors"
          >
            <i className="ri-close-line text-xl text-earth-500"></i>
          </button>
        </div>

        {/* Content */}
        <div className="p-5">
          {/* Basic Info */}
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-olive-100 rounded-xl flex items-center justify-center">
              <span className="text-2xl font-bold text-olive-700">
                {student.fullName.charAt(0)}
              </span>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-earth-800">
                {student.fullName}
              </h3>
              <p className="text-earth-700 text-sm">{student.email}</p>
            </div>
          </div>

          {/* Personal Info Section */}
          <div className="mb-6">
            <h4 className="text-sm font-medium text-earth-700 uppercase tracking-wide mb-3">
              Personal Information
            </h4>
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 bg-earth-50 rounded-lg">
                <div className="w-8 h-8 bg-earth-100 rounded-lg flex items-center justify-center">
                  <i className="ri-phone-line text-earth-600"></i>
                </div>
                <div>
                  <p className="text-xs text-earth-700">Phone</p>
                  <p className="text-earth-800 font-medium">
                    {student.phone || "Not provided"}
                  </p>
                </div>
                {student.phone && (
                  <a
                    href={`https://wa.me/${student.phone.replace(/\D/g, "")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-auto p-2 bg-emerald-100 hover:bg-emerald-200 text-emerald-600 rounded-lg transition-colors"
                  >
                    <i className="ri-whatsapp-line"></i>
                  </a>
                )}
              </div>

              {student.startDate && (
                <div className="flex items-center gap-3 p-3 bg-earth-50 rounded-lg">
                  <div className="w-8 h-8 bg-earth-100 rounded-lg flex items-center justify-center">
                    <i className="ri-calendar-line text-earth-600"></i>
                  </div>
                  <div>
                    <p className="text-xs text-earth-700">Start Date</p>
                    <p className="text-earth-800 font-medium">
                      {formatDate(student.startDate)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Level & Class Section */}
          <div className="mb-6">
            <h4 className="text-sm font-medium text-earth-700 uppercase tracking-wide mb-3">
              Level and Class
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-olive-50 border border-olive-100 rounded-lg">
                <p className="text-xs text-olive-600">Level</p>
                <p className="text-lg font-bold text-olive-800">
                  {student.levelCode || "-"}
                </p>
                {student.levelName && (
                  <p className="text-xs text-olive-600">{student.levelName}</p>
                )}
              </div>
              <div className="p-3 bg-earth-50 border border-earth-100 rounded-lg">
                <p className="text-xs text-earth-700">Class</p>
                <p className="text-sm font-medium text-earth-800 truncate">
                  {student.classGroupName || "No class"}
                </p>
                {student.classType && (
                  <p className="text-xs text-earth-700 truncate capitalize">
                    {student.classType}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Payment Section */}
          <div className="mb-6">
            <h4 className="text-sm font-medium text-earth-700 uppercase tracking-wide mb-3">
              Financial Status
            </h4>
            <div
              className={`p-4 rounded-xl border ${
                paymentInfo.status === "paid"
                  ? "bg-emerald-50 border-emerald-100"
                  : paymentInfo.status === "overdue"
                    ? "bg-red-50 border-red-100"
                    : paymentInfo.status === "pending"
                      ? "bg-amber-50 border-amber-100"
                      : "bg-earth-50 border-earth-100"
              }`}
            >
              <div className="flex items-center justify-between mb-3">
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-lg text-sm font-medium ${paymentInfo.bgClass} ${paymentInfo.textClass}`}
                >
                  {paymentInfo.label}
                </span>
                {student.paymentSummary?.currentModuleStatus && (
                  <span className="text-sm text-earth-600 capitalize">
                    Módulo {student.paymentSummary.currentModuleStatus}
                  </span>
                )}
              </div>

              {student.paymentSummary ? (
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-earth-700 text-xs mb-1">Paid</p>
                    <p className="font-semibold text-emerald-600">
                      {student.paymentSummary.totalPaid}
                    </p>
                  </div>
                  <div>
                    <p className="text-earth-700 text-xs mb-1">Pending</p>
                    <p className="font-semibold text-amber-600">
                      {student.paymentSummary.totalPending}
                    </p>
                  </div>
                  <div>
                    <p className="text-earth-700 text-xs mb-1">Overdue</p>
                    <p className="font-semibold text-red-600">
                      {student.paymentSummary.totalOverdue}
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-earth-700 text-sm">
                  Payment data not available
                </p>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-2">
            {student.email && (
              <a
                href={`mailto:${student.email}`}
                className="flex items-center justify-center gap-2 w-full py-2.5 bg-earth-600 hover:bg-earth-700 text-white rounded-lg font-medium transition-colors"
              >
                <i className="ri-mail-line"></i>
                Send Email
              </a>
            )}
            {student.phone && (
              <a
                href={`https://wa.me/${student.phone.replace(/\D/g, "")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors"
              >
                <i className="ri-whatsapp-line"></i>
                WhatsApp
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function TeacherStudentsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const levelFilter = searchParams.get("level") || "";
  const classFilter = searchParams.get("class") || "";
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStudent, setSelectedStudent] =
    useState<StudentForTeacher | null>(null);

  const {
    data: studentsData,
    isLoading,
    error,
    refetch,
  } = useTeacherStudents({
    level: levelFilter || undefined,
    classGroup: classFilter || undefined,
  });

  // Auto-sync receipts from Google Drive on page load
  const { isSyncing } = useSyncReceipts({
    autoSync: true,
  });

  // Refetch students data after sync completes
  useEffect(() => {
    if (!isSyncing && studentsData) {
      refetch();
    }
  }, [isSyncing]);

  // Fetch classes for the filter dropdown
  const { data: classesData } = useTeacherClasses({
    level: levelFilter || undefined,
  });

  // Get unique class groups from the classes data
  const classGroups = useMemo(() => {
    if (!classesData) return [];
    return classesData.map((cg) => ({
      id: cg.id,
      name: cg.name,
    }));
  }, [classesData]);

  const handleLevelChange = (level: string) => {
    const params = new URLSearchParams(searchParams);
    if (level) {
      params.set("level", level);
    } else {
      params.delete("level");
    }
    params.delete("class"); // Reset class filter when level changes
    setSearchParams(params);
  };

  const handleClassChange = (classId: string) => {
    const params = new URLSearchParams(searchParams);
    if (classId) {
      params.set("class", classId);
    } else {
      params.delete("class");
    }
    setSearchParams(params);
  };

  // Payment summary stats - derived from payment counts
  const paymentStats = useMemo(() => {
    if (!studentsData?.students) return null;
    return {
      // Paid: No overdue AND has at least 1 paid
      paid: studentsData.students.filter(
        (s) =>
          s.paymentSummary &&
          s.paymentSummary.totalOverdue === 0 &&
          s.paymentSummary.totalPaid > 0,
      ).length,
      // Pending: No overdue, no paid, only pending
      pending: studentsData.students.filter(
        (s) =>
          s.paymentSummary &&
          s.paymentSummary.totalOverdue === 0 &&
          s.paymentSummary.totalPaid === 0 &&
          s.paymentSummary.totalPending > 0,
      ).length,
      // Overdue: Has at least 1 overdue
      overdue: studentsData.students.filter(
        (s) => s.paymentSummary && s.paymentSummary.totalOverdue > 0,
      ).length,
    };
  }, [studentsData]);

  // Filter students locally by search query
  const students = useMemo(() => {
    const allStudents = studentsData?.students || [];

    if (!searchQuery.trim()) {
      return allStudents;
    }

    const query = searchQuery.toLowerCase();
    return allStudents.filter(
      (student) =>
        student.fullName.toLowerCase().includes(query) ||
        student.email.toLowerCase().includes(query),
    );
  }, [studentsData?.students, searchQuery]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-sand-50 flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-sand-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-earth-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <i className="ri-error-warning-line text-2xl text-earth-400"></i>
          </div>
          <p className="text-earth-600 font-medium">Error loading students</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Header */}
        <div className="mb-6">
          <Link
            to="/teacher"
            className="inline-flex items-center gap-1 text-earth-700 hover:text-earth-700 text-sm mb-2 transition-colors"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Dashboard
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-display font-bold text-earth-800">
                Students
              </h1>
              <p className="text-earth-700 text-sm mt-1">
                View all student data, classes and financial status
              </p>
            </div>
            {isSyncing && (
              <div className="flex items-center gap-2 px-3 py-2 bg-olive-50 border border-olive-200 rounded-lg">
                <div className="animate-spin">
                  <i className="ri-refresh-line text-olive-600"></i>
                </div>
                <span className="text-sm text-olive-700 font-medium">
                  Syncing receipts...
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Payment Summary Cards */}
        {paymentStats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 mb-6">
            <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3 sm:p-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center">
                  <i className="ri-check-line text-white"></i>
                </div>
                <div>
                  <p className="text-xs text-emerald-600">Paid</p>
                  <p className="text-xl font-bold text-emerald-800">
                    {paymentStats.paid}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 sm:p-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center">
                  <i className="ri-time-line text-white"></i>
                </div>
                <div>
                  <p className="text-xs text-amber-600">Pending</p>
                  <p className="text-xl font-bold text-amber-800">
                    {paymentStats.pending}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-red-50 border border-red-100 rounded-xl p-3 sm:p-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-red-500 rounded-lg flex items-center justify-center">
                  <i className="ri-error-warning-line text-white"></i>
                </div>
                <div>
                  <p className="text-xs text-red-600">Overdue</p>
                  <p className="text-xl font-bold text-red-800">
                    {paymentStats.overdue}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl border border-earth-200 p-4 mb-6">
          <div className="flex flex-wrap gap-4">
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name or email..."
                  className="w-full pl-10 pr-4 py-2 bg-earth-50 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500"
                />
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-earth-400"></i>
              </div>
            </div>

            {/* Level Filter */}
            <select
              value={levelFilter}
              onChange={(e) => handleLevelChange(e.target.value)}
              className="px-3 py-2 bg-earth-50 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500"
            >
              <option value="">All levels</option>
              {LEVELS.map((level) => (
                <option key={level} value={level}>
                  {level}
                </option>
              ))}
            </select>

            {/* Class Filter */}
            <select
              value={classFilter}
              onChange={(e) => handleClassChange(e.target.value)}
              className="px-3 py-2 bg-earth-50 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500"
            >
              <option value="">All classes</option>
              {classGroups.map((cg) => (
                <option key={cg.id} value={cg.id}>
                  {cg.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Students Table */}
        <div className="bg-white rounded-xl border border-earth-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-earth-100">
            <p className="text-sm text-earth-600">
              <span className="font-medium">{students.length}</span> student(s)
              found
            </p>
          </div>

          {students.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-earth-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-earth-700 uppercase tracking-wide">
                      Student
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-earth-700 uppercase tracking-wide">
                      Level
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-earth-700 uppercase tracking-wide">
                      Class
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-earth-700 uppercase tracking-wide">
                      Description
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-earth-700 uppercase tracking-wide">
                      Payment
                    </th>
                    <th className="px-4 py-3 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((student) => (
                    <StudentRow
                      key={student.id}
                      student={student}
                      onClick={() => setSelectedStudent(student)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <i className="ri-user-search-line text-4xl text-earth-300 mb-2"></i>
              <p className="text-earth-700">No students found</p>
              {(searchQuery || levelFilter || classFilter) && (
                <button
                  onClick={() => {
                    setSearchQuery("");
                    setSearchParams(new URLSearchParams());
                  }}
                  className="mt-2 text-olive-600 hover:text-olive-700 text-sm font-medium"
                >
                  Clear filters
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Student Detail Panel */}
      {selectedStudent && (
        <StudentDetailPanel
          student={selectedStudent}
          onClose={() => setSelectedStudent(null)}
        />
      )}
    </div>
  );
}

export default TeacherStudentsPage;
